import { TAX_FREQUENCY_LABELS, type TaxCalendarRule } from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Input,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { ExpensesPage } from "../../../features/expenses/shared.js";
import {
  useRefreshTaxCalendar,
  useTaxCalendarCrumb,
  useTaxCalendarPermissions,
  useTaxCalendarRules,
} from "../../../features/tax-calendar/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/vergi-takvimi/kurallar")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: RulesPage,
});

interface RuleDraft {
  id: string;
  name: string;
  dueDay: string;
  dueMonthOffset: string;
  reminderDays: string;
  needsVerification: boolean;
  isActive: boolean;
  version: number;
}

function RulesPage(): ReactElement {
  const { t } = useTranslation();
  const crumb = useTaxCalendarCrumb();
  return (
    <ExpensesPage
      title={t("taxCalendar.rules.title")}
      description={t("taxCalendar.rules.description")}
      breadcrumb={[crumb, { label: t("taxCalendar.rules.title") }]}
    >
      {({ companyId }) => <RulesTable companyId={companyId} />}
    </ExpensesPage>
  );
}

function RulesTable({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTaxCalendar();
  const permissions = useTaxCalendarPermissions();
  const rules = useTaxCalendarRules(companyId, true);
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (input: RuleDraft) =>
      api.PATCH("/tax-calendar-rules/{id}", {
        params: { path: { id: input.id } },
        body: {
          name: input.name.trim(),
          dueDay: Number(input.dueDay || "1"),
          dueMonthOffset: Number(input.dueMonthOffset || "1"),
          reminderDays: input.reminderDays
            .split(",")
            .map((part) => Number(part.trim()))
            .filter((value) => Number.isInteger(value) && value >= 0 && value <= 90),
          needsVerification: input.needsVerification,
          isActive: input.isActive,
          version: input.version,
        } as never,
      }),
    onSuccess: async () => {
      setDraft(null);
      await refresh();
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const columns = useMemo<DataTableColumn<TaxCalendarRule>[]>(
    () => [
      {
        id: "name",
        header: t("taxCalendar.fields.ruleName"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            {permissions.canManage ? (
              <button
                type="button"
                className="font-medium underline underline-offset-4"
                onClick={() =>
                  setDraft({
                    id: row.id,
                    name: row.name,
                    dueDay: String(row.dueDay),
                    dueMonthOffset: String(row.dueMonthOffset),
                    reminderDays: row.reminderDays.join(", "),
                    needsVerification: row.needsVerification,
                    isActive: row.isActive,
                    version: row.version,
                  })
                }
              >
                {row.name}
              </button>
            ) : (
              <span className="font-medium">{row.name}</span>
            )}
            {/* Mevzuat parametresi seed'den geldiyse "doğrulanmalı" işaretlidir (AGENTS.md §3.12). */}
            {row.needsVerification ? (
              <StatusBadge tone="warning">{t("taxCalendar.rules.needsVerification")}</StatusBadge>
            ) : null}
          </span>
        ),
      },
      { id: "code", header: t("taxCalendar.fields.code"), cell: (row) => row.code },
      {
        id: "frequency",
        header: t("taxCalendar.fields.frequency"),
        cell: (row) => TAX_FREQUENCY_LABELS[row.frequency],
      },
      {
        id: "dueDay",
        header: t("taxCalendar.fields.dueDay"),
        align: "center",
        cell: (row) => String(row.dueDay),
      },
      {
        id: "dueMonthOffset",
        header: t("taxCalendar.fields.dueMonthOffset"),
        align: "center",
        cell: (row) => String(row.dueMonthOffset),
      },
      {
        id: "reminderDays",
        header: t("taxCalendar.fields.reminderDays"),
        cell: (row) => row.reminderDays.join(", ") || "—",
      },
      {
        id: "openObligationCount",
        header: t("taxCalendar.fields.openObligations"),
        align: "center",
        cell: (row) => String(row.openObligationCount),
      },
      {
        id: "isActive",
        header: t("common.status"),
        hideable: false,
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
    ],
    [t, permissions.canManage],
  );

  return (
    <div className="grid gap-4">
      <Alert tone="info" title={t("taxCalendar.rules.verifyTitle")}>
        {t("taxCalendar.rules.verifyBody")}
      </Alert>
      <DataTable
        caption={t("taxCalendar.rules.title")}
        columns={columns}
        rows={rules.data ?? []}
        rowKey={(row) => row.id}
        loading={rules.isPending}
        error={rules.error ? <Alert tone="danger">{errorMessage(rules.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<CalendarClock />}
            title={t("taxCalendar.rules.emptyTitle")}
            description={t("taxCalendar.rules.emptyDescription")}
          />
        }
      />

      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
        title={t("taxCalendar.rules.edit")}
        description={t("taxCalendar.rules.editDescription")}
      >
        {draft ? (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              save.mutate(draft);
            }}
          >
            {error ? <Alert tone="danger">{error}</Alert> : null}
            <FormField label={t("taxCalendar.fields.ruleName")} required>
              {(control) => (
                <Input
                  {...control}
                  required
                  value={draft.name}
                  maxLength={150}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              )}
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label={t("taxCalendar.fields.dueDay")}
                required
                description={t("taxCalendar.fields.dueDayHint")}
              >
                {(control) => (
                  <Input
                    {...control}
                    type="number"
                    min={1}
                    max={31}
                    value={draft.dueDay}
                    onChange={(event) => setDraft({ ...draft, dueDay: event.target.value })}
                  />
                )}
              </FormField>
              <FormField
                label={t("taxCalendar.fields.dueMonthOffset")}
                description={t("taxCalendar.fields.dueMonthOffsetHint")}
              >
                {(control) => (
                  <Input
                    {...control}
                    type="number"
                    min={0}
                    max={12}
                    value={draft.dueMonthOffset}
                    onChange={(event) => setDraft({ ...draft, dueMonthOffset: event.target.value })}
                  />
                )}
              </FormField>
            </div>
            <FormField
              label={t("taxCalendar.fields.reminderDays")}
              description={t("taxCalendar.fields.reminderDaysHint")}
            >
              {(control) => (
                <Input
                  {...control}
                  value={draft.reminderDays}
                  onChange={(event) => setDraft({ ...draft, reminderDays: event.target.value })}
                />
              )}
            </FormField>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={!draft.needsVerification}
                onChange={(event) => setDraft({ ...draft, needsVerification: !event.target.checked })}
              />
              {t("taxCalendar.rules.markVerified")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.isActive}
                onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
              />
              {t("common.active")}
            </label>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={save.isPending}>
                {t("common.save")}
              </Button>
            </div>
          </form>
        ) : null}
      </Dialog>
    </div>
  );
}
